import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/auth/AuthContext'
import { ReviewDecision } from './ReviewDecision'
import { usePeerReviewDraft } from './usePeerReviewDraft'
import type { ReviewDetail } from './types'
import { ReturnToExpertConfirmation } from './ReturnToExpertConfirmation'

/** Engineers make the final transition; other reviewers submit advisory recommendations. */
export function EngineerDecision({
  data,
  busy,
  action,
  onDirty,
}: {
  data: ReviewDetail
  busy: boolean
  action: (path: string, body?: unknown) => Promise<boolean | undefined>
  onDirty: (dirty: boolean) => void
}) {
  const { user } = useAuth()
  const { draft, update, clear } = usePeerReviewDraft(user?.id ?? '', data.round.round_id)
  const { verdict, feedback } = draft
  const rebuild = !!data.quality_changes?.rebuild_required
  const generatedRequest = data.can_decide && (rebuild || verdict === 'needs_changes') ? data.quality_changes?.send_back_message ?? '' : ''
  const previousRequest = useRef('')
  useEffect(() => {
    if (!generatedRequest) {
      if (previousRequest.current && feedback === previousRequest.current) update('feedback', '')
      previousRequest.current = ''; return
    }
    if (verdict !== 'needs_changes') update('verdict', 'needs_changes')
    if (generatedRequest !== previousRequest.current) {
      const old = previousRequest.current
      const next = feedback.includes(generatedRequest) ? feedback
        : old && feedback.includes(old) ? feedback.replace(old, generatedRequest)
        : feedback.trim() ? `${feedback}\n\n${generatedRequest}` : generatedRequest
      previousRequest.current = generatedRequest
      update('feedback', next)
    }
  }, [generatedRequest, feedback, verdict, update])
  const [confirming, setConfirming] = useState(false)
  const returnButton = useRef<HTMLButtonElement>(null)
  const canReturnToExpert = user?.role === 'engineer' && data.can_decide &&
    data.round.kind === 'quality' && !data.submission.task.scientific && verdict === 'needs_changes'
  useEffect(() => {
    onDirty((!!verdict || !!feedback) && !(generatedRequest && verdict === 'needs_changes' && feedback === generatedRequest))
    return () => onDirty(false)
  }, [verdict, feedback, generatedRequest, onDirty])
  async function submit() {
    if (!verdict || (verdict === 'needs_changes' && !feedback.trim() && !rebuild)) return
    if (data.can_decide && verdict === 'ready' && rebuild) return
    const base = `/tasks/${data.round.task_id}`
    const success = data.can_decide
      ? await action(`${base}/transition`, {
          action: verdict === 'ready' ? 'forward' : 'send_back',
          revision: data.task_revision,
          round_id: data.round.round_id,
          review_version: data.round.version,
          feedback,
        })
      : await action(`${base}/reviews/${data.round.round_id}/recommendations`, {
          version: data.round.version,
          verdict,
          feedback,
        })
    if (success) clear()
  }
  async function returnToExpert() {
    if (!canReturnToExpert || busy || !feedback.trim()) return
    const success = await action(`/tasks/${data.round.task_id}/transition`, {
      action: 'return_to_expert', revision: data.task_revision,
      round_id: data.round.round_id, review_version: data.round.version, feedback,
    })
    setConfirming(false)
    if (success) clear()
  }
  return (
    <ReviewDecision
      quality
      busy={busy}
      readyDisabled={rebuild}
      feedbackRequired
      submitted={false}
      draft={draft}
      update={update}
      submit={submit}
      title={data.can_decide ? 'Your decision' : 'Your review'}
      submitDisabled={
        data.can_decide && verdict === 'ready' && (!data.submission.task.authoring_submission || !!data.publication_blockers?.length)
      }
      submitLabel={
        data.can_decide
          ? verdict === 'ready'
            ? data.submission.task.scientific ? 'Accept quality check' : 'Approve and publish'
            : verdict === 'needs_changes'
              ? data.submission.task.scientific ? 'Return to Build' : 'Send back to agent'
              : 'Submit decision'
          : undefined
      }
    >
      {data.can_decide && rebuild && <p className="muted small">Please send back to agent. Ready is unavailable until the task package is rebuilt.</p>}
      {data.publication_blockers?.map(message => <p className="note small" key={message}>{message}</p>)}
      {(verdict || feedback) && <button className="link small" disabled={busy} onClick={() => {
        if (generatedRequest) { update('verdict', 'needs_changes'); update('feedback', generatedRequest) }
        else clear()
      }}>{generatedRequest ? 'Reset feedback' : 'Discard draft'}</button>}
      {canReturnToExpert && <div className="review-return-expert">
        <button ref={returnButton} disabled={busy || !feedback.trim()} onClick={() => setConfirming(true)}>
          Return to expert execution
        </button>
        {confirming && <ReturnToExpertConfirmation busy={busy} feedback={feedback} returnFocus={returnButton.current}
          onCancel={() => setConfirming(false)} onConfirm={returnToExpert} />}
      </div>}
    </ReviewDecision>
  )
}
